import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import { type Task, task } from 'ember-concurrency';
import { pushAndReturnPayload } from 'frontend-utils/utils/ember-data';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import type HotelSettingsMyPropertyGeneralDefaultsController from 'lumen/controllers/hotel/settings/my-property/general-defaults';
import type SubscriptionChannelDistribution from 'lumen/models/subscription/channel-distribution';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { Transition } from 'lumen/services/transition';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import { UNIFIED_SETTINGS_FEATURES } from 'lumen/utils/constants/unified-settings-feature-constants';
import protectedRoute from 'lumen/utils/decorators/protected-route';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';
import {
  setupUnsavedChangesTransitionHandler,
  type TransitionHandler,
} from 'lumen/utils/unsaved-changes-transition-handlers';

interface SubscriptionChannelDistributionResponse {
  currency: string;
  room_price_vat_rate: number | null;
}

export interface GeneralDefaultsModel {
  requestChannelDistributionTask: Task<SubscriptionChannelDistribution, []>;
}

// @ts-expect-error: Unable to resolve signature of class decorator when called as an expression.
@protectedRoute({
  features: [
    UNIFIED_SETTINGS_FEATURES.CHANNEL_DISTRIBUTION_VIEW,
    UNIFIED_SETTINGS_FEATURES.CHANNEL_DISTRIBUTION_EDIT,
  ],
  redirectTo: 'hotel.settings',
})
export default class HotelSettingsMyPropertyGeneralDefaultsRoute extends Route {
  @service declare ajax: AjaxService;
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare router: Services['router'];
  @service declare store: Services['store'];

  #transitionHandler?: TransitionHandler;

  requestChannelDistributionTask: Task<SubscriptionChannelDistribution, []> =
    task(async () => {
      try {
        const subscriptionId = this.hotelAndMarketService.hotel.id!;

        const url = formatUrl(
          `/${APIGATEWAY}/configure/subscription/channel-distribution`,
          { subscription_id: subscriptionId },
          this.ajax.host,
        );

        const response =
          await this.ajax.request<SubscriptionChannelDistributionResponse>(
            url,
            { method: 'GET' },
          );

        const result = pushAndReturnPayload(
          this.store,
          {
            'subscription-channel-distributions': [
              { ...response, id: subscriptionId },
            ],
          },
          'subscription/channel-distribution',
        );

        return (
          result[
            'subscription-channel-distributions'
          ] as SubscriptionChannelDistribution[]
        )[0]!;
      } catch (error) {
        this.errorTracking.captureException(error);
        throw new SilentTaskError(getErrorMessage(error));
      }
    });

  deactivate(): void {
    this.#transitionHandler?.cleanup();
    this.#transitionHandler = undefined;

    void this.requestChannelDistributionTask.cancelAll();

    if (this.store.isDestroyed || this.store.isDestroying) {
      return;
    }

    this.store.unloadAll('subscription/channel-distribution');
  }

  model(): GeneralDefaultsModel {
    void this.requestChannelDistributionTask.perform();

    return {
      requestChannelDistributionTask: this.requestChannelDistributionTask,
    };
  }

  resetController(
    controller: HotelSettingsMyPropertyGeneralDefaultsController,
    isExiting: boolean,
    transition: Transition,
  ): void {
    super.resetController(controller, isExiting, transition);

    if (isExiting && this.#transitionHandler) {
      this.#transitionHandler?.cleanup();
      this.#transitionHandler = undefined;
    }
  }

  setupController(
    controller: HotelSettingsMyPropertyGeneralDefaultsController,
    model: GeneralDefaultsModel,
    transition: Transition,
  ): void {
    super.setupController(controller, model, transition);
    this.#transitionHandler = setupUnsavedChangesTransitionHandler(
      this.router,
      controller,
      'hotel.settings.my-property.general-defaults',
    );
  }
}
